import React, { createContext, useState, useContext, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const CanvasContext = createContext();

export const useCanvas = () => useContext(CanvasContext);

export const CanvasProvider = ({ children }) => {
  const [mode, setMode] = useState('edit'); // 'edit' or 'view'
  const [blocks, setBlocks] = useState(() => {
    try {
      const saved = localStorage.getItem('bookmark-canvas-blocks');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to load blocks", e);
      return [];
    }
  });
  const [history, setHistory] = useState([]); // Array of blocks arrays
  const [snapToGrid, setSnapToGrid] = useState(false);
  
  // Load initial view state
  const initialView = (() => {
      try {
          const saved = localStorage.getItem('bookmark-canvas-view');
          return saved ? JSON.parse(saved) : { x: 0, y: 0, zoom: 1 };
      } catch (e) {
          return { x: 0, y: 0, zoom: 1 };
      }
  })();

  const [zoom, setZoom] = useState(initialView.zoom);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const canvasPositionRef = React.useRef({ x: initialView.x, y: initialView.y });

  useEffect(() => {
    localStorage.setItem('bookmark-canvas-blocks', JSON.stringify(blocks));
  }, [blocks]);

  // Persist zoom changes
  useEffect(() => {
      const currentView = {
          x: canvasPositionRef.current.x,
          y: canvasPositionRef.current.y,
          zoom
      };
      localStorage.setItem('bookmark-canvas-view', JSON.stringify(currentView));
  }, [zoom]);

  const saveCanvasPosition = (x, y) => {
      canvasPositionRef.current = { x, y };
      const currentView = {
          x,
          y,
          zoom
      };
      localStorage.setItem('bookmark-canvas-view', JSON.stringify(currentView));
  };

  const saveHistory = () => {
    setHistory((prev) => [...prev, blocks]);
  };

  const undo = () => {
    if (history.length > 0) {
      const previousBlocks = history[history.length - 1];
      setBlocks(previousBlocks);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const copyBlock = () => {
    if (selectedBlockId) {
        const block = blocks.find(b => b.id === selectedBlockId);
        if (block) {
            setClipboard({ type: block.type, data: block.data });
        }
    }
  };

  const pasteBlock = () => {
    if (clipboard) {
        addBlock(clipboard.type, clipboard.data);
    }
  };

  const updateCanvasPosition = (x, y) => {
    canvasPositionRef.current = { x, y };
  };

  const addBlock = (type, data, position) => {
    saveHistory();
    let spawnPos = position;
    
    if (!spawnPos) {
        // Calculate center of current view
        const { x, y } = canvasPositionRef.current;
        const centerX = (window.innerWidth / 2 - x) / zoom;
        const centerY = (window.innerHeight / 2 - y) / zoom;
        
        spawnPos = { 
            x: centerX - 150, // Center the 300px wide block
            y: centerY - 75   // Center the 150px tall block
        };
    }

    const newBlock = {
      id: uuidv4(),
      type, 
      data, 
      position: spawnPos,
      size: { width: 300, height: 150 }, // Default size
      zIndex: blocks.length + 1,
    };
    setBlocks((prev) => [...prev, newBlock]);
  };

  const updateBlock = (id, updates, save = true) => {
    if (save) saveHistory();
    setBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  };

  const removeBlock = (id) => {
    saveHistory();
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const toggleMode = () => {
    setMode((prev) => (prev === 'edit' ? 'view' : 'edit'));
  };

  const toggleSnapToGrid = () => {
    setSnapToGrid((prev) => !prev);
  };

  const bringToFront = (id) => {
    saveHistory();
    const maxZ = Math.max(...blocks.map((b) => b.zIndex || 0), 0);
    updateBlock(id, { zIndex: maxZ + 1 }, false); // Don't save history twice
  };

  const sendToBack = (id) => {
    saveHistory();
    const minZ = Math.min(...blocks.map((b) => b.zIndex || 0), 0);
    updateBlock(id, { zIndex: minZ - 1 }, false); // Don't save history twice
  }

  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));

  return (
    <CanvasContext.Provider
      value={{
        mode,
        toggleMode,
        blocks,
        addBlock,
        updateBlock,
        removeBlock,
        snapToGrid,
        toggleSnapToGrid,
        bringToFront,
        sendToBack,
        zoom,
        setZoom,
        zoomIn,
        zoomOut,
        updateCanvasPosition,
        saveCanvasPosition,
        initialView,
        undo,
        canUndo: history.length > 0,
        selectedBlockId,
        setSelectedBlockId,
        copyBlock,
        pasteBlock
      }}
    >
      {children}
    </CanvasContext.Provider>
  );
};
